/* eslint no-template-curly-in-string: 0 */
import { notification as notify } from 'utils'
import R from 'ramda'

// A Slack webhook is an http(s) URL with a real (dotted) host. Parse it so
// a scheme-only value like "https://" or "https:// text" is rejected.
const isValidWebhookUrl = value => {
  try {
    const parsed = new window.URL(value)
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.hostname.includes('.')
    )
  } catch (err) {
    return false
  }
}

export default function ({ ownerId, values }) {
  const { type: experienceType } = values
  if (
    experienceType === 'INSTAGRAM_COMPETITION' ||
    experienceType === 'FACEBOOK_COMPETITION' ||
    experienceType === 'FACEBOOK_REMINDER' ||
    experienceType === 'FACEBOOK_RECURRING_REMINDER' ||
    experienceType === 'FACEBOOK_COUPON' ||
    experienceType === 'INSTAGRAM_COUPON' ||
    experienceType === 'TWITTER_THREADVENTURE'
  ) {
    return
  }
  const validationError = message => {
    notify.error({
      title: 'Validation Error',
      message
    })
    return { _error: message }
  }

  // A nameless campaign that still holds posts is a half-filled create-new
  // choice; blocking it stops an unnamed campaign reaching widget_data.
  const campaign = R.path(['intentBased', 'campaign'], values)
  const campaignPosts = R.pathOr(
    [],
    ['intentBased', 'campaign', 'posts'],
    values
  )
  if (campaign && campaignPosts.length && !(campaign.name || '').trim()) {
    return validationError('Name the campaign, or remove its posts')
  }

  // A Pulse Intelligence campaign post with no post ID cannot be matched or
  // resolve its permalink for ${missedPostUrl}, so block save.
  const blankPostIndex = campaignPosts.findIndex(
    post => !(post.postId || '').trim()
  )
  if (blankPostIndex !== -1) {
    return validationError(
      `Campaign post ${blankPostIndex + 1} needs a post ID, or remove it`
    )
  }

  // Tiered Responses & Points save guards. ${tierResponse} is honoured only on
  // first-class (success) actions, so a fallback (failure) action using it is
  // invalid; a tier that a response action references by ${tierResponse} must
  // have a response; a tier with neither a response nor points is a no-op.
  const tiers = R.pathOr([], ['intentBased', 'tierResponses'], values)
  const pointsEnabled =
    R.path(['intentBased', 'tierPointsEnabled'], values) === true
  const actions = values.actions || []
  const usesTierResponse = list =>
    /\$\{tierResponse\}/.test(JSON.stringify(list))
  const firstClass = R.chain(action => action.success || [], actions)
  const fallback = R.chain(action => action.failure || [], actions)
  const str = value => String(value == null ? '' : value).trim()
  const tierMergeUsed = usesTierResponse(firstClass)

  // ${tierResponse} is first-class only and only a tier can resolve it. These
  // hold whatever the tier count, so they sit outside the per-tier loop: a
  // fallback must never use it, and a success action using it needs at least
  // one tier to supply the text.
  if (usesTierResponse(fallback)) {
    return validationError(
      '${tierResponse} can only be used on first-class actions, not fallback actions'
    )
  }
  if (tierMergeUsed && !tiers.length) {
    return validationError(
      'A response action uses ${tierResponse} but no tiers are configured'
    )
  }

  if (tiers.length) {
    const tierHasResponse = tier =>
      tier.useRandom
        ? (tier.randomResponses || []).some(r => str(r) !== '')
        : str(tier.response) !== ''
    const tierHasPoints = tier => pointsEnabled && str(tier.points) !== ''

    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i]
      const label = tier.name || `Tier ${i + 1}`

      if (tierMergeUsed && !tierHasResponse(tier)) {
        return validationError(
          `${label}: a response action uses \${tierResponse} but this tier has no response`
        )
      }
      if (!tierHasResponse(tier) && !tierHasPoints(tier)) {
        return validationError(
          `${label}: add a response or points, it currently does nothing`
        )
      }
      if (
        tier.useRandom &&
        (tier.randomResponses || []).some(r => str(r) === '')
      ) {
        return validationError(
          `${label}: every random response variant must have text`
        )
      }
      if (pointsEnabled) {
        const points = str(tier.points)
        const pointsValue = Number(points)
        if (points === '' || !Number.isFinite(pointsValue) || pointsValue < 0) {
          return validationError(
            `${label}: set a points value of 0 or more (points are enabled)`
          )
        }
      }
      const min = str(tier.minLength)
      const max = str(tier.maxLength)
      const minValue = Number(min)
      const maxValue = Number(max)
      if (
        (min !== '' && (!Number.isFinite(minValue) || minValue < 0)) ||
        (max !== '' && (!Number.isFinite(maxValue) || maxValue < 0))
      ) {
        return validationError(`${label}: length bounds must be 0 or more`)
      }
      if (min !== '' && max !== '' && minValue > maxValue) {
        return validationError(
          `${label}: minimum length cannot exceed the maximum`
        )
      }
    }
  }

  // Repeat-engagement fallback: when enabled it needs copy, and must not use
  // ${tierResponse} (first-class only; ${totalPoints} and friends are allowed).
  // The section is gated behind Enable Points in the UI, so skip it when points
  // are off; otherwise a stale enabled flag blocks save for a hidden field.
  const pointsOn = R.path(['intentBased', 'tierPointsEnabled'], values) === true
  const repeatResponse = R.pathOr({}, ['intentBased', 'repeatResponse'], values)
  if (pointsOn && repeatResponse.enabled === true) {
    if (!String(repeatResponse.response || '').trim()) {
      return validationError(
        'Repeat engagement: add the reply copy to send on a repeat (the toggle is on)'
      )
    }
    if (/\$\{tierResponse\}/.test(repeatResponse.response)) {
      return validationError(
        'Repeat engagement: remove ${tierResponse} (it is for first-class actions only)'
      )
    }
  }

  // Special-moment CS alert: an enabled alert that cannot run is worse than
  // none, so block save when the prompt is missing (nothing to detect) or the
  // chosen channel has no / an invalid destination (CS silently never alerted).
  const specialMoment = R.pathOr({}, ['intentBased', 'specialMoment'], values)
  if (specialMoment.enabled === true) {
    if (!String(specialMoment.prompt || '').trim()) {
      return validationError(
        'Special moment: describe the moment in the prompt (the toggle is on)'
      )
    }
    const channel = specialMoment.channel || 'cs-alerts'
    if (channel === 'slack') {
      const url = String(specialMoment.slackWebhookUrl || '').trim()
      if (!url) {
        return validationError('Special moment: add the Slack webhook URL')
      }
      if (!isValidWebhookUrl(url)) {
        return validationError(
          'Special moment: the Slack webhook URL must be a valid URL'
        )
      }
    } else if (channel === 'email') {
      const email = String(specialMoment.email || '').trim()
      if (!email) {
        return validationError('Special moment: add the alert email address')
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        return validationError(
          'Special moment: the alert email address must be valid'
        )
      }
    }
  }

  // Missed-post redirect reply: when the toggle is on the reply must carry
  // copy and stay within a tweet's length, otherwise a missed post would
  // redirect to an empty or over-long reply that never sends.
  const TWEET_MAX = 280
  const trim = value => String(value == null ? '' : value).trim()
  const redirectReplies = R.chain(
    action => action.success || [],
    values.actions || []
  ).filter(action => action.hasMissedPostRedirect)
  if (
    redirectReplies.some(
      a => !trim(R.path(['missedPostRedirect', 'message'], a))
    )
  ) {
    return validationError(
      'Missed-post redirect: add the reply copy to send on a missed post (the toggle is on)'
    )
  }
  if (
    redirectReplies.some(
      a => trim(R.path(['missedPostRedirect', 'message'], a)).length > TWEET_MAX
    )
  ) {
    return validationError(
      `Missed-post redirect: the reply must be ${TWEET_MAX} characters or fewer`
    )
  }

  const vulnerableTriggers = ['MENTION', 'HASHTAG', 'KEYWORD']

  const isVulnerableTrigger = R.both(
    R.pipe(R.prop('type'), R.contains(R.__, vulnerableTriggers)),
    R.pipe(R.prop('data'), R.isEmpty)
  )

  const isNotVulnerableTrigger = R.complement(isVulnerableTrigger)

  // remove empty trigger data for above mentioned types
  // @param action - the action sets
  const removeEmptyTriggerData = R.map(
    R.evolve({
      triggers: R.filter(isNotVulnerableTrigger)
    })
  )

  values.actions = removeEmptyTriggerData(values.actions)

  const hasPrimaryTriggers =
    values.actions.length &&
    values.actions.every(
      action => action.triggers.length && action.triggers.some(t => t.primary)
    )

  if (!hasPrimaryTriggers) {
    return validationError('You must specify triggers')
  }

  const successActionsMissing = values.actions.some(
    action => !action.success.length
  )

  if (successActionsMissing) {
    return validationError('You must specify actions')
  }

  //  Ensure that if a trigger set contains a hashtag it is combined with a mention
  const getAllTriggers = R.map(action => action.triggers)
  const getTriggerTypes = R.map(trigger => R.pluck('type')(trigger))

  const allTriggers = getAllTriggers(values.actions)
  const triggerTypes = getTriggerTypes(allTriggers)

  let triggerSetsValid = true

  triggerTypes.forEach(triggerTypeSet => {
    const hasHashtag = R.contains('HASHTAG')(triggerTypeSet)
    const hasMention = R.either(
      R.contains('MENTION'),
      R.contains('FROM')
    )(triggerTypeSet)

    if (hasHashtag && !hasMention) triggerSetsValid = false
  })

  if (!triggerSetsValid) {
    return validationError(
      `Trigger sets containing Hashtags must be combined with Mentions or Owner's Tweet in order to save`
    )
  }

  // The evaluator reads the CTA post from the first MATCH_INTENT trigger only,
  // so a second one is silently ignored. Allow at most one across the widget.
  // Validation runs on the mapped widget shape, so the type is MATCH_INTENT.
  const matchIntentCount = R.sum(
    triggerTypes.map(set => set.filter(type => type === 'MATCH_INTENT').length)
  )
  if (matchIntentCount > 1) {
    return validationError(
      'Only one Match Intent trigger is allowed; the extra one is ignored'
    )
  }

  if (!ownerId) {
    return validationError('Widget must have an owner in order to save')
  }
}



// WEBPACK FOOTER //
// ./src/pages/widgets/validation.js